import { useEffect, useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import api from "../../services/api";
import { PaginatedResponse, User, Tenant, TenantMembership } from "../../api/types";

const PAGE_LIMIT = 12;

export function Users() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [userTenants, setUserTenants] = useState<Record<number, TenantMembership[]>>({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddToTenantModal, setShowAddToTenantModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", roleId: 2 });
  const [addToTenantData, setAddToTenantData] = useState({ tenantId: 0, role: "member" });
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchEmailInput, setSearchEmailInput] = useState("");
  const [tenantFilterInput, setTenantFilterInput] = useState<number | null>(null);
  const [appliedSearchEmail, setAppliedSearchEmail] = useState("");
  const [appliedTenantId, setAppliedTenantId] = useState<number | null>(null);

  const logError = (label: string, error: unknown) => {
    if (isAxiosError(error)) {
      console.warn(label, error.response?.status ?? "unknown", error.message);
      return;
    }
    if (error instanceof Error) {
      console.warn(label, error.message);
      return;
    }
    console.warn(label);
  };

  useEffect(() => {
    loadUsers(1, "", null);
    loadTenants();
  }, []);

  async function loadUsers(pageToLoad = 1, emailFilter = appliedSearchEmail, tenantFilter = appliedTenantId) {
    try {
      const query = new URLSearchParams();
      query.set("limit", String(PAGE_LIMIT));
      query.set("page", String(pageToLoad));
      if (emailFilter.trim()) {
        query.set("email", emailFilter.trim());
      }
      if (tenantFilter) {
        query.set("tenantId", String(tenantFilter));
      }
      const { data } = await api.get<PaginatedResponse<User>>(`/users?${query.toString()}`);
      setUsers(data.data);
      setPage(data.meta.page);
      setTotalPages(data.meta.totalPages);
      setTotalUsers(data.meta.total);
      await loadTenantsForUsers(data.data);
    } catch (error) {
      logError("Failed to load users", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTenants() {
      try {
          const { data } = await api.get<Tenant[]>("/tenants/all");
          setTenants(data);
      } catch (error) {
          logError("Failed to load tenants", error);
      }
  }

  // Busca as empresas que um usuário específico participa para exibir/remover.
  async function loadTenantsForUsers(list: User[]) {
    if (!list.length) {
      setUserTenants({});
      return;
    }
    try {
      const responses = await Promise.all(
        list.map(async (user) => ({
          userId: user.id,
          memberships: (await api.get<TenantMembership[]>(`/tenants/user/${user.id}/memberships`)).data,
        }))
      );
      const mapped: Record<number, TenantMembership[]> = {};
      responses.forEach(({ userId, memberships }) => {
        mapped[userId] = memberships;
      });
      setUserTenants(mapped);
    } catch (error) {
      logError("Failed to load user tenants", error);
    }
  }

  async function refreshUserTenants(userId: number) {
    try {
      const { data } = await api.get<TenantMembership[]>(`/tenants/user/${userId}/memberships`);
      setUserTenants((prev) => ({ ...prev, [userId]: data }));
    } catch (error) {
      logError("Failed to refresh user tenants", error);
    }
  }

  async function handleCreateUser(e: FormEvent) {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      setShowCreateModal(false);
      setNewUser({ name: "", email: "", password: "", roleId: 2 });
      loadUsers(page);
    } catch (error) {
      logError("Failed to create user", error);
      const message =
        (error as any)?.response?.data?.error ||
        (error as any)?.message ||
        t("admin.users.createError");
      alert(message);
    }
  }

  async function handleDeleteUser(id: number) {
    if (!confirm(t("admin.users.deleteConfirm"))) return;
    try {
      await api.delete(`/users/${id}`);
      loadUsers(page);
    } catch (error) {
      logError("Failed to delete user", error);
      alert(t("admin.users.deleteError"));
    }
  }

  async function handleToggleStatus(user: User) {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      if (!confirm(t("admin.users.statusConfirm", { status: newStatus }))) return;
      try {
          await api.put(`/users/${user.id}`, { status: newStatus });
          loadUsers(page);
      } catch (error) {
          logError("Failed to update status", error);
          alert(t("admin.users.statusError"));
      }
  }

  async function handleResetMFA(user: User) {
    if (!confirm(t("admin.users.resetMfaConfirm", { name: user.name }))) return;
    try {
      await api.post(`/mfa/users/${user.id}/disable`);
      setActionMessage(t("admin.users.mfaReset", { name: user.name }));
      loadUsers(page);
    } catch (error) {
      logError("Failed to reset MFA", error);
      alert(t("admin.users.resetMfaError"));
    }
  }

  // Permite ao administrador redefinir a senha de um usuário diretamente.
  async function handleChangePassword(user: User) {
      const newPassword = prompt(t("admin.users.changePasswordPrompt", { name: user.name }));
      if (!newPassword) return;
      if (newPassword.length < 12) {
          alert(t("admin.users.passwordMin"));
          return;
      }
      try {
          await api.put(`/users/${user.id}`, { password: newPassword });
          alert(t("admin.users.passwordUpdated"));
      } catch (error) {
          logError("Failed to change password", error);
          const message =
            (error as any)?.response?.data?.error ||
            (error as any)?.message ||
            t("admin.users.passwordError");
          alert(message);
      }
  }

  async function handleAddToTenant(e: FormEvent) {
      e.preventDefault();
      if (!selectedUser || addToTenantData.tenantId === 0) return;
      
      try {
          await api.post(`/tenants/${addToTenantData.tenantId}/users`, {
              user_id: selectedUser.id,
              role: addToTenantData.role
          });
          alert(t("admin.users.addedToTenant"));
          setShowAddToTenantModal(false);
          refreshUserTenants(selectedUser.id);
      } catch (error) {
          logError("Failed to add user to tenant", error);
          alert(t("admin.users.addToTenantError"));
      }
  }

  function openAddToTenantModal(user: User) {
      setSelectedUser(user);
      setAddToTenantData({ tenantId: tenants.length > 0 ? tenants[0].id : 0, role: "member" });
      setShowAddToTenantModal(true);
  }

  // Remove a associação do usuário com uma empresa específica.
  async function handleRemoveFromTenant(tenantId: number, userId: number) {
      if (!confirm("Remover este usuário da empresa?")) return;
      try {
          await api.delete(`/tenants/${tenantId}/users/${userId}`);
          refreshUserTenants(userId);
      } catch (error) {
          logError("Failed to remove user from tenant", error);
          alert(t("admin.users.removeFromTenantError"));
      }
  }

  async function handleChangeTenantRole(tenantId: number, userId: number, role: string) {
    try {
      await api.put(`/tenants/${tenantId}/users/${userId}/role`, { role });
      refreshUserTenants(userId);
    } catch (error) {
      logError("Failed to change tenant role", error);
      alert(t("admin.users.updateRoleError"));
    }
  }

  if (loading) return <p>{t("admin.users.loading")}</p>;

  return (
    <div className="shell">
      <div className="header">
        <div className="header-title">
          <h1>{t("admin.users.title")}</h1>
          <p className="muted">{t("admin.users.subtitle")}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const normalizedEmail = searchEmailInput.trim();
              setAppliedSearchEmail(normalizedEmail);
              setAppliedTenantId(tenantFilterInput);
              loadUsers(1, normalizedEmail, tenantFilterInput);
            }}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
          >
            <input
              value={searchEmailInput}
              onChange={(e) => setSearchEmailInput(e.target.value)}
              placeholder={t("admin.users.searchPlaceholder")}
              style={{ width: '220px' }}
            />
            <select
              className="secondary"
              value={tenantFilterInput ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setTenantFilterInput(value ? Number(value) : null);
              }}
              style={{ height: '40px' }}
            >
              <option value="">{t("admin.users.allTenants")}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
            <button className="secondary" type="submit">
              {t("admin.users.search")}
            </button>
            {(appliedSearchEmail || appliedTenantId) && (
              <button
                className="secondary"
                type="button"
                onClick={() => {
                  setSearchEmailInput("");
                  setTenantFilterInput(null);
                  setAppliedSearchEmail("");
                  setAppliedTenantId(null);
                  loadUsers(1, "", null);
                }}
              >
                {t("admin.users.clear")}
              </button>
            )}
          </form>
          <button onClick={() => setShowCreateModal(true)}>
            <span style={{ marginRight: '0.5rem' }}>+</span> {t("admin.users.newUser")}
          </button>
        </div>
      </div>
      
      {actionMessage && (
        <div className="info" style={{ marginBottom: '1.5rem' }}>
          {actionMessage}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem' }}>{t("admin.users.newUserTitle")}</h3>
            <form onSubmit={handleCreateUser} className="stack">
              <label>
                {t("admin.users.name")}
                <input
                  value={newUser.name}
                  placeholder={t("admin.users.namePlaceholder")}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("admin.users.email")}
                <input
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  type="email"
                  placeholder={t("admin.users.emailPlaceholder")}
                  required
                />
              </label>
              <label>
                {t("admin.users.password")}
                <input
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  type="password"
                  placeholder={t("admin.users.passwordPlaceholder")}
                  required
                  minLength={12}
                />
              </label>
              <label>
                {t("admin.users.role")}
                <select 
                  className="secondary"
                  value={newUser.roleId} 
                  onChange={(e) => setNewUser({ ...newUser, roleId: Number(e.target.value) })}
                  style={{ height: '42px' }}
                >
                  <option value={1}>{t("admin.users.roleAdmin")}</option>
                  <option value={2}>{t("admin.users.roleUser")}</option>
                </select>
              </label>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="submit" style={{ flex: 1 }}>{t("admin.users.create")}</button>
                <button type="button" className="secondary" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddToTenantModal && selectedUser && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px', padding: '2rem' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>{t("admin.users.linkTenantTitle")}</h3>
            <p className="muted" style={{ marginBottom: '1.5rem' }}>
              {t("admin.users.userLabel")}: <strong>{selectedUser.name}</strong>
            </p>
            <form onSubmit={handleAddToTenant} className="stack">
              <label>
                {t("admin.users.tenant")}
                <select 
                  className="secondary"
                  value={addToTenantData.tenantId} 
                  onChange={(e) => setAddToTenantData({ ...addToTenantData, tenantId: Number(e.target.value) })}
                  style={{ height: '42px' }}
                >
                  {tenants.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              <label>
                {t("admin.users.tenantRole")}
                <select 
                  className="secondary"
                  value={addToTenantData.role} 
                  onChange={(e) => setAddToTenantData({ ...addToTenantData, role: e.target.value })}
                  style={{ height: '42px' }}
                >
                  <option value="member">{t("admin.users.tenantRoleMember")}</option>
                  <option value="admin">{t("admin.users.tenantRoleAdmin")}</option>
                </select>
              </label>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="submit" disabled={tenants.length === 0} style={{ flex: 1 }}>
                  {t("admin.users.link")}
                </button>
                <button type="button" className="secondary" style={{ flex: 1 }} onClick={() => setShowAddToTenantModal(false)}>
                  {t("common.cancel")}
                </button>
              </div>
              {tenants.length === 0 && (
                <p className="error" style={{ marginTop: '0.5rem' }}>{t("admin.users.noTenants")}</p>
              )}
            </form>
          </div>
        </div>
      )}

      <div className="grid">
        {users.map((user) => (
          <div key={user.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0 }}>{user.name}</h3>
                <p className="muted" style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>{user.email}</p>
              </div>
              <span style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '6px', 
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  backgroundColor: user.status === 'active' ? '#f0fdf4' : '#fef2f2',
                  color: user.status === 'active' ? '#16a34a' : '#ef4444',
                  border: `1px solid ${user.status === 'active' ? '#dcfce7' : '#fee2e2'}`
              }}>
                  {user.status}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <p className="muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.25rem' }}>
                  {t("admin.users.access")}
                </p>
                <p style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  {user.roleId === 1 ? t("admin.users.roleAdmin") : t("admin.users.roleUser")}
                </p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.25rem' }}>
                  {t("admin.users.mfaSecurity")}
                </p>
                <p style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '500',
                  color: user.mfaEnabled ? '#16a34a' : '#f59e0b'
                }}>
                  {user.mfaEnabled ? t("admin.users.mfaActive") : t("admin.users.mfaPending")}
                </p>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.8125rem', color: 'var(--text-main)' }}>
                  {t("admin.users.linkedTenants")}
                </strong>
                <button className="secondary small" onClick={() => openAddToTenantModal(user)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                  {t("admin.users.link")}
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {userTenants[user.id]?.length ? (
                  userTenants[user.id].map((tenant) => (
                    <div key={tenant.tenantId} style={{ 
                      backgroundColor: '#f8fafc', 
                      padding: '0.75rem', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between'
                    }}>
                      <div>
                        <p style={{ fontSize: '0.8125rem', fontWeight: '600', margin: 0 }}>{tenant.tenantName}</p>
                        <select
                          value={tenant.role}
                          onChange={(e) => handleChangeTenantRole(tenant.tenantId, user.id, e.target.value)}
                          style={{ 
                            fontSize: '0.7rem', 
                            border: 'none', 
                            background: 'none', 
                            color: 'var(--accent)', 
                            padding: 0, 
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="member">{t("admin.users.tenantRoleMember")}</option>
                          <option value="admin">{t("admin.users.tenantRoleAdmin")}</option>
                        </select>
                      </div>
                      <button
                        onClick={() => handleRemoveFromTenant(tenant.tenantId, user.id)}
                        style={{ background: 'none', color: '#94a3b8', fontSize: '1.25rem', padding: '0 0.5rem' }}
                        title={t("common.remove")}
                      >×</button>
                    </div>
                  ))
                ) : (
                  <p className="muted" style={{ fontSize: '0.75rem', fontStyle: 'italic', textAlign: 'center', padding: '0.5rem' }}>
                    {t("admin.users.noLinkedTenants")}
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <button className="secondary small" onClick={() => handleChangePassword(user)} style={{ fontSize: '0.75rem' }}>
                    {t("admin.users.newPassword")}
                </button>
                <button className="secondary small" onClick={() => handleResetMFA(user)} style={{ fontSize: '0.75rem' }}>
                    {t("admin.users.resetMfa")}
                </button>
                <button 
                    className={`small ${user.status === 'active' ? 'secondary' : ''}`}
                    onClick={() => handleToggleStatus(user)}
                    style={{ fontSize: '0.75rem' }}
                >
                    {user.status === 'active' ? t("common.deactivate") : t("common.activate")}
                </button>
                <button 
                  className="secondary small" 
                  onClick={() => handleDeleteUser(user.id)} 
                  style={{ fontSize: '0.75rem', color: '#ef4444', borderColor: '#fecaca' }}
                >
                  {t("common.delete")}
                </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {t("admin.users.pageSummary", { page, totalPages, total: totalUsers })}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="secondary"
            disabled={page <= 1}
            onClick={() => loadUsers(page - 1, appliedSearchEmail, appliedTenantId)}
          >
            {t("common.previous")}
          </button>
          <button
            className="secondary"
            disabled={page >= totalPages}
            onClick={() => loadUsers(page + 1, appliedSearchEmail, appliedTenantId)}
          >
            {t("common.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
